var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => {
  __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
  return value;
};
import Base from "../../Base.js";
import Fencible from "../../mixin/Fencible.js";
const { floor, max, min } = Math, constrain = (v, v0, v1) => min(max(v, v0), v1);
class Axis extends Base.mixin(Fencible) {
  get direction() {
    const { name } = this, { events, scrollStarted } = this.owner;
    let d = 0, i, v, xy;
    for (i = events.length; i-- > 0; ) {
      xy = events[i][name];
      if (v != null) {
        d = v - xy;
        if (d) {
          break;
        }
      }
      v = xy;
    }
    if (!d && v != null) {
      d = v - scrollStarted[name];
    }
    return d;
  }
  get drift() {
    return this._drift || 0;
  }
  get driftRange() {
    const { range } = this;
    return [range[0], max(range[0], range[1] - this.scrollSize)];
  }
  get items() {
    var _a;
    return (_a = this.owner) == null ? void 0 : _a.items;
  }
  get safeZone() {
    const { drift, scrollSize } = this, safetyMargin = floor(this.owner.safetyMargin * scrollSize);
    return [drift + safetyMargin, drift + scrollSize - safetyMargin];
  }
  get scroller() {
    var _a, _b;
    return (_b = (_a = this.owner) == null ? void 0 : _a.client) == null ? void 0 : _b.scrollable;
  }
  get scrollLimit() {
    var _a;
    return (_a = this.owner) == null ? void 0 : _a.scrollLimit;
  }
  get vast() {
    const { range } = this;
    return range ? range[1] - range[0] > this.scrollSize : false;
  }
  changePos(pos) {
    const { range } = this;
    return constrain(pos || 0, range[0], range[1]);
  }
  updatePos(pos) {
    this.sync({ pos });
  }
  updateDrift(drift) {
    const me = this, { pos, range } = me, scrollPos = pos - drift;
    if (me.scrollPos === scrollPos) {
      const event = new CustomEvent("scroll", { detail: "driftChange" });
      me.owner.scroller.element.dispatchEvent(event);
    } else if (range[0] <= pos && pos < range[1]) {
      me.scrollPos = scrollPos;
    }
  }
  changeRange(v, was) {
    const [lo, hi] = Array.isArray(v) ? v : [0, v], ret = [lo || 0, hi == null ? Infinity : hi];
    if (!was || ret[0] !== was[0] || ret[1] !== was[1]) {
      return ret;
    }
  }
  updateRange(range) {
    this.scrollSize = range[1] - range[0];
    this.sync({ range });
  }
  changeScrollSize(value) {
    return min(value, this.scrollLimit);
  }
  snap() {
    const me = this, { direction, items, owner, pos, range } = me;
    let item, p0, p1, snap, snapThreshold;
    for (item of items) {
      [p0, p1] = me.getItemSpan(item);
      if (p0 <= pos && pos < p1) {
        if (p0 < pos) {
          snapThreshold = owner.snapThreshold;
          if (snapThreshold < 1) {
            snapThreshold = snapThreshold * (p1 - p0);
          }
          snapThreshold = max(1, snapThreshold);
          if (direction < 0) {
            snap = p1 - pos > snapThreshold ? p0 : p1;
          } else {
            snap = pos - p0 > snapThreshold ? p1 : p0;
          }
          snap = constrain(snap, range[0], range[1]);
          me.pos = snap;
          owner.watchForEnd();
        } else {
          owner.scrolling = false;
        }
        break;
      }
    }
  }
  sync(updates) {
    const me = Object.assign(this, updates), { _drift: driftWas, range, vast } = me;
    if (range && range[0] !== range[1]) {
      let { pos } = me, drift;
      const outOfRange = pos < range[0] || pos >= range[1];
      if (pos == null || outOfRange) {
        pos = range[1] < 0 ? range[0] : constrain(0, range[0], range[1]);
      }
      if (!vast) {
        drift = range[0];
      } else {
        const { driftRange, safeZone, scrollSize } = me;
        if (driftWas == null || pos < safeZone[0] || pos > safeZone[1]) {
          drift = constrain(floor(pos - scrollSize / 2), driftRange[0], driftRange[1]);
        } else {
          drift = driftWas;
        }
      }
      me.scroller.positionDirty = true;
      me.overflow = "hidden-scroll";
      me.drift = drift;
      if (drift !== driftWas) {
        if (outOfRange) {
          me.positionDirty = false;
          me._pos = null;
          me.pos = pos;
        } else {
          me.positionDirty = true;
        }
      } else if ((updates == null ? void 0 : updates.pos) != null) {
        me.scrollPos = pos - drift;
        me.positionDirty = true;
      } else {
        if (me.positionDirty) {
          me.positionDirty = false;
          me._pos = null;
        }
        me.pos = me.scrollPos + drift;
      }
      if ((updates == null ? void 0 : updates.range) || drift !== driftWas) {
        me.owner.syncItems();
      }
    } else {
      me.overflow = "hidden";
    }
  }
  toLogical(physical) {
    return physical + this.drift;
  }
  /**
   * Returns the physical scroll position for the given logical scroll position. Valid physical scroll positions are
   * not negative. This method returns NaN for logical scroll positions that cannot be converted to a valid, physical
   * scroll position.
   * @param {Number} logical
   * @returns {Number}
   */
  toPhysical(logical) {
    const physical = logical - this.drift;
    return physical >= 0 && physical < this.scrollLimit ? physical : NaN;
  }
}
__publicField(Axis, "configurable", {
  /**
   * The owning `InfinityScroller` for this axis.
   *
   * @config {Core.helper.util.InfinityScroller}
   */
  owner: null,
  /**
   * The delta between a logical scroll position and physical scroll position. In other words, adding the drift
   * to a logical scroll position, produces the corresponding physical scroll position.
   * @config {Number}
   */
  drift: null,
  overflow: null,
  /**
   * The logical scroll position.
   * @config {Number}
   */
  pos: {
    value: null,
    default: 0,
    $config: "lazy"
  },
  /**
   * The logical scroll range as a two element array containing the range's lower bound and upper bound,
   * respectively.
   * @config {Number[]}
   */
  range: {
    value: null,
    $config: "lazy"
  },
  scrollSize: null
});
__publicField(Axis, "fenced", {
  sync: true
});
class XAxis extends Axis {
  get name() {
    return "x";
  }
  get other() {
    var _a;
    return (_a = this.owner) == null ? void 0 : _a.y;
  }
  get scrollPos() {
    return this.scroller.x;
  }
  set scrollPos(v) {
    this.scroller.x = v;
  }
  get scrollSize() {
    return this.scroller.scrollWidth;
  }
  set scrollSize(v) {
    super.scrollSize = v;
  }
  getItemSpan(item) {
    const vxy = item.$vxy, { x } = vxy;
    return [x, x + vxy.width];
  }
  updateScrollSize(v) {
    this.scroller.scrollWidth = v;
  }
  updateOverflow(overflow) {
    this.scroller.overflowX = overflow;
  }
}
class YAxis extends Axis {
  get name() {
    return "y";
  }
  get other() {
    var _a;
    return (_a = this.owner) == null ? void 0 : _a.x;
  }
  get scrollPos() {
    return this.scroller.y;
  }
  set scrollPos(v) {
    this.scroller.y = v;
  }
  get scrollSize() {
    return this.scroller.scrollHeight;
  }
  set scrollSize(v) {
    super.scrollSize = v;
  }
  getItemSpan(item) {
    const vxy = item.$vxy, { y } = vxy;
    return [y, y + vxy.height];
  }
  updateScrollSize(v) {
    this.scroller.scrollHeight = v;
  }
  updateOverflow(overflow) {
    this.scroller.overflowY = overflow;
  }
}
Axis.X = XAxis;
Axis.Y = YAxis;
Axis._$name = "Axis";
export {
  Axis as default
};
